import React, { useEffect, useState } from 'react';
import { Grid, Card, CardMedia, CardContent, Typography, Slide, Button, Skeleton, Fade, Box, Divider, Collapse, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { getDatabase, ref, get, query, orderByChild, limitToLast } from 'firebase/database';
import firebaseApp, { camara, KEY_REF } from './adm/admin/firebaseConfig';
import { useTheme } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

function removePTags(html) {
  if (!html) return '';

  return html
    .replace(/<\/?(p|h1|h2|strong|b)[^>]*>/gi, '')
    .replace(/<\/?a[^>]*>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<ol>.*?<\/ol>/gis, '')
    .replace(/<li>.*?<\/li>/gis, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
export default function Noticias({ config }) {
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // Menor que 960px
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Menor que 600px

  const handleExpandClick = (id) => {
    setExpanded((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  useEffect(() => {
    const fetchNoticias = async () => {
      setLoading(true);
      if (!KEY_REF) {
        console.error("KEY_REF não definido.");
        setLoading(false);
        return;
      }
      try {
        const databaseInstance = getDatabase(firebaseApp);
        const noticiasRef = query(ref(databaseInstance, `noticias`), orderByChild('data'), limitToLast(8));
        const snapshot = await get(noticiasRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const noticiasArray = Object.keys(data).map((key) => ({
            id: key,
            titulo: data[key].titulo,
            descricao: data[key].descricao,
            imagem: data[key].imagem,
            data: data[key].data,
          }));
          setNoticias(noticiasArray.reverse());
        } else {
          setNoticias([]);
        }
      } catch (error) {
        console.error("Erro ao buscar notícias:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNoticias();
  }, [KEY_REF]);

  const renderSkeleton = (index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
      <Skeleton variant="rectangular" height={isVerySmallScreen ? 150 : isSmallScreen ? 200 : 300} />
      <Skeleton variant="text" />
      <Skeleton variant="text" width="60%" />
    </Grid>
  );

  const renderNoticiaCard = (noticia) => {
    const descricaoSemPTags = removePTags(noticia.descricao);
    const tituloParaURL = noticia.titulo
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, '-')
      .toLowerCase();

    // Formatação básica de data amigável se disponível
    const dataFormatada = noticia.data ? new Date(noticia.data).toLocaleDateString('pt-BR') : 'Notícia';

    return (
      <Grid item xs={12} sm={6} md={6} lg={3} key={noticia.id} sx={{ display: 'flex' }}>
        <Fade in={!loading} timeout={800}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              borderRadius: '10px',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), boxShadow 0.3s ease',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                '& .noticia-media': {
                  transform: 'scale(1.06)',
                }
              },
            }}
          >
            <Link
              to={`/noticias/${tituloParaURL}/${noticia.id}`}
              style={{ textDecoration: 'none', color: 'inherit', overflow: 'hidden', display: 'block', position: 'relative' }}
            >
              <Box sx={{ overflow: 'hidden', position: 'relative', pt: '56.25%' /* Aspect Ratio 16:9 */ }}>
                <CardMedia
                  component="img"
                  className="noticia-media"
                  image={noticia.imagem}
                  alt={noticia.titulo}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                  }}
                />
              </Box>
            </Link>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
              {/* Tag de Data / Categoria */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5, opacity: 0.7 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: config?.color }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {dataFormatada}
                </Typography>
              </Box>

              <Link to={`/noticias/${tituloParaURL}/${noticia.id}`} style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
                <Typography
                  sx={{
                    fontSize: '1.05rem',
                    lineHeight: 1.35,
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 700,
                    color: '#1a1a1a',
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    height: '2.7em', // Garante alinhamento de altura mesmo com títulos curtos
                    '&:hover': { color: config?.color }
                  }}
                >
                  {noticia.titulo}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    textAlign: 'justify',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    mb: 2
                  }}
                >
                  {descricaoSemPTags}
                </Typography>
              </Link>

              <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link to={`/noticias/${tituloParaURL}/${noticia.id}`} style={{ textDecoration: 'none' }}>
                  <Button
                    size="small"
                    endIcon={<LaunchIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      color: config?.color,
                      fontSize: '0.8rem',
                      padding: '4px 0px',
                      '&:hover': { backgroundColor: 'transparent', opacity: 0.8 }
                    }}
                  >
                    Ler matéria completa
                  </Button>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>
    );
  };

  const renderUltimasNoticias = () => {
    const ultimasNoticias = noticias.slice(0, 8);
    return loading ? [...Array(8)].map((_, index) => renderSkeleton(index)) : ultimasNoticias.map(renderNoticiaCard);
  };

  const renderServiceCard = (service) => {
    if (!service.active) return null;

    const linkProps = service.external
      ? { component: 'a', href: service.href, target: '_blank', rel: 'noopener noreferrer' }
      : { to: service.href };

    if (!service.href) {
      console.warn(`Href vazio ou ausente para o serviço: ${service.name}`);
    }

    return (
      <Grid item xs={6} sm={4} md={6} key={service.name} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          sx={{
            boxShadow: 3,
            '&:hover': { boxShadow: 6 },
            textAlign: 'center',
            padding: 2,
            height: isVerySmallScreen ? '80px' : isSmallScreen ? '100px' : '140px',
            width: isVerySmallScreen ? '80px' : isSmallScreen ? '100px' : '140px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Link {...linkProps} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CardMedia
              component="img"
              image={`/imagens/icons/${service.icon}`}
              alt={service.name}
              sx={{
                width: isVerySmallScreen ? 40 : isSmallScreen ? 50 : 70,
                height: isVerySmallScreen ? 40 : isSmallScreen ? 50 : 70,
                marginTop: 3.2,
              }}
            />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 0 }}>
              <Typography
                variant="subtitle1"
                style={{
                  fontSize: isVerySmallScreen ? '0.6em' : isSmallScreen ? '0.7em' : '0.85em',
                  textAlign: 'center',
                }}
              >
                {service.name}
              </Typography>
            </CardContent>
          </Link>
        </Card>
      </Grid>
    );
  };

  const acessoRapidoData = (config?.acessoRapido || []).filter((service) => service.active);

  return (
    <Box sx={{ backgroundColor: '#f9f9f9', paddingTop: 5, paddingBottom: 5, paddingX: { xs: 1, sm: 2, md: 10 } }}>
      <Grid container spacing={isVerySmallScreen ? 1 : isSmallScreen ? 1.5 : 2.5}>
        <Grid item xs={12} md={config?.design?.acessoRapido ? 10 : 12}>
          <Box textAlign="center" mb={4}>
            <Typography
              sx={{
                fontSize: isVerySmallScreen ? 20 : isSmallScreen ? 24 : 28,
                color: config.color,
                fontWeight: 'bold',
              }}
              gutterBottom
            >
              FIQUE INFORMADO
            </Typography>
            <Divider
              sx={{
                backgroundColor: config?.color,
                height: 2,
                marginBottom: 4,
                marginX: 'auto',
                width: { xs: '50%', md: '80%' },
              }}
            />
          </Box>
          <Grid container spacing={isVerySmallScreen ? 1 : isSmallScreen ? 1.5 : 2.5} sx={{ paddingX: { xs: 1, md: 10 } }}>
            {renderUltimasNoticias()}
          </Grid>
        </Grid>
        {config?.design?.acessoRapido && (
          <Grid item xs={12} md={2}>
            <Box textAlign="center" mb={4}>
              <Typography
                sx={{
                  fontSize: isVerySmallScreen ? 20 : isSmallScreen ? 24 : 28,
                  color: config.color,
                  fontWeight: 'bold',
                }}
                gutterBottom
              >
                ACESSO RÁPIDO
              </Typography>
              <Divider sx={{ backgroundColor: config?.color, height: 2, marginBottom: 3 }} />
            </Box>
            <Grid container spacing={isVerySmallScreen ? 0.5 : 1}>
              {acessoRapidoData.map(renderServiceCard)}
            </Grid>
          </Grid>
        )}
      </Grid>

      <Box mt={2} textAlign="center">
        <Link to="/noticias/todas">
          <Button
            variant="contained"
            size="medium"
            startIcon={<LaunchIcon />}
            sx={{
              backgroundColor: config?.color,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              '&:hover': { backgroundColor: config?.colorSecondary, color: config?.color },
              padding: isVerySmallScreen ? '6px 16px' : '8px 20px',
              borderRadius: '30px',
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              fontSize: isVerySmallScreen ? 12 : 14,
            }}
          >
            TODAS AS NOTÍCIAS
          </Button>
        </Link>
      </Box>
    </Box>
  );
}